import { InputAdornment, MenuItem, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Colors, getSafeValueInArray, getSafeValueInObject } from '@rentguru/commons-utils';
import { useFormikContext, type FormikProps } from 'formik';
import { clamp, isEmpty, isNil, toNumber } from 'lodash';

import { TextDetailEditableProps, useTextFieldStyles } from './textFieldUtils';

const useDisabledStyles = makeStyles({
  label: {
    color: Colors.LIGHT_BLUE_GREY,
  },
  underline: {
    '&:before': {
      borderBottom: `2px solid ${Colors.TOWER_GREY}`,
    },
  },
  root: {
    '&$disabled:before': {
      borderBottomStyle: 'hidden',
    },
    '&$disabled': {
      backgroundColor: Colors.PORCELAIN_GREY_1,
    },
  },
  disabled: {
    backgroundColor: Colors.PORCELAIN_GREY_1,
  },
  adornmentRoot: {
    '&$disabled': {
      backgroundColor: Colors.PORCELAIN_GREY_1,
    },
  },
});

const getClampedNumberValue = (value: string, min: number, max: number) => {
  const convertedValue = toNumber(value);
  if (Number.isNaN(convertedValue)) {
    return value;
  }
  const endsWithCommaZero = value.endsWith(',0') || value.endsWith('.0');
  const clamped = clamp(convertedValue, min, max);
  if (endsWithCommaZero && clamped === convertedValue) {
    return value;
  }
  return clamped;
};

const disablePasswordManagerInputProps = {
  'data-lpignore': 'true',
  'data-1p-ignore': 'true',
  'data-bwignore': 'true',
  'data-form-type': 'other',
};

export const TextDetailEditable: React.FC<TextDetailEditableProps> = ({
  title,
  text,
  children,
  editMode,
  name,
  error = false,
  type = 'text',
  endAdornment,
  endAdornmentStyle,
  idInArray,
  arrayName,
  selectable = false,
  selectableValues = [],
  style = {},
  inputProps = {},
  noMaxWidth = false,
  onChangeObserver,
  disabled = false,
  min = Number.MIN_SAFE_INTEGER,
  max = Number.MAX_SAFE_INTEGER,
  step = 1,
  helperText,
  multiline,
  rows,
  rowsMax,
  forcedValue,
  typeNumberNoEndArrow = false,
  titleStyle = {},
  useDefaultOnChange = true,
  dataTest = '',
  placeholder = undefined,
  modern = false,
}) => {
  const classes = useTextFieldStyles();
  const disabledClasses = useDisabledStyles();
  const formik = useFormikContext();
  if (!formik) {
    return null;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { values, handleChange, handleBlur, setFieldValue }: FormikProps<any> = formik;

  const isNumberField = type === 'number';

  const endAdornmentProps = isNil(endAdornment)
    ? null
    : {
        endAdornment: (
          <InputAdornment style={endAdornmentStyle} position="end">
            {endAdornment}
          </InputAdornment>
        ),
      };
  if (disabled && editMode && isNil(forcedValue)) {
    const inputValue =
      isNil(arrayName) || isNil(idInArray)
        ? getSafeValueInObject(values, name)
        : getSafeValueInArray(values[arrayName], idInArray, name);

    if (modern) {
      return (
        <div style={{ width: '100%' }}>
          {!isEmpty(title) && (
            <Typography className={classes.cardFieldLabel} style={titleStyle}>
              {title}
            </Typography>
          )}
          <TextField
            name={name}
            type={type}
            disabled
            value={inputValue.toString()}
            margin="dense"
            variant="outlined"
            fullWidth
            style={{ maxWidth: noMaxWidth ? 2500 : 280, top: 0, width: '100%', ...style }}
            error={error}
            inputProps={{ min, max, step, ...disablePasswordManagerInputProps }}
            InputProps={{
              ...endAdornmentProps,
              ...inputProps,
              classes: { root: classes.outlinedRoot },
            }}
            helperText={helperText}
            multiline={multiline}
            minRows={rows}
            maxRows={rowsMax}
            data-test={`textDetail${dataTest}`}
            onWheel={(e) => {
              if (isNumberField && e.target instanceof HTMLElement) {
                e.target.blur();
              }
            }}
            placeholder={placeholder}
          />
        </div>
      );
    }

    return (
      <TextField
        label={title}
        name={name}
        type={type}
        disabled
        value={inputValue.toString()}
        margin="dense"
        variant="filled"
        style={{ maxWidth: noMaxWidth ? 2500 : 280, top: 0, width: '100%', ...style }}
        error={error}
        inputProps={{
          min,
          max,
          step,
          ...disablePasswordManagerInputProps,
        }}
        InputProps={{
          ...endAdornmentProps,
          ...inputProps,
          classes: {
            underline: disabledClasses.underline,
            root: disabledClasses.root,
            disabled: disabledClasses.disabled,
          },
        }}
        InputLabelProps={{ className: disabledClasses.label }}
        helperText={helperText}
        multiline={multiline}
        minRows={rows}
        maxRows={rowsMax}
        hiddenLabel={isEmpty(title)}
        data-test={`textDetail${dataTest}`}
        onWheel={(e) => {
          if (isNumberField && e.target instanceof HTMLElement) {
            e.target.blur();
          }
        }}
        placeholder={placeholder}
      />
    );
  }
  if (editMode && !selectable && isNil(forcedValue)) {
    const getFieldValue = () => {
      if (isNil(arrayName) || isNil(idInArray)) {
        return getSafeValueInObject(values, name).toString();
      }
      return getSafeValueInArray(values[arrayName], idInArray, name).toString();
    };

    const currentValue = getFieldValue();

    const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const targetValue = e.target ? e.target.value : e.currentTarget.value;
      const value = isNumberField && targetValue !== '' ? getClampedNumberValue(targetValue, min, max) : targetValue;
      if (isNil(arrayName) || isNil(idInArray)) {
        if (useDefaultOnChange) {
          setFieldValue(name, value);
        }
      } else {
        setFieldValue(
          arrayName,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          values[arrayName].map((o: any) => {
            if (o.id === idInArray) {
              return { ...o, [name]: value };
            }
            return o;
          })
        );
      }
      if (!isNil(onChangeObserver)) {
        onChangeObserver(value);
      }
    };

    const handleWheelBlur = (e: React.WheelEvent) => {
      if (isNumberField && e.target instanceof HTMLElement) {
        e.target.blur();
      }
    };

    if (modern) {
      return (
        <div style={{ width: '100%' }}>
          {!isEmpty(title) && (
            <Typography className={classes.cardFieldLabel} style={titleStyle}>
              {title}
            </Typography>
          )}
          <TextField
            id={name}
            type={type}
            name={name}
            margin="dense"
            variant="outlined"
            fullWidth
            value={currentValue}
            onChange={handleFieldChange}
            onBlur={handleBlur}
            error={error}
            style={{ maxWidth: noMaxWidth ? 2500 : 280, width: '100%', top: 0, ...style }}
            inputProps={{ min, max, step, ...disablePasswordManagerInputProps }}
            InputProps={{
              ...endAdornmentProps,
              ...inputProps,
              classes: typeNumberNoEndArrow
                ? { root: classes.outlinedRoot, input: classes.noEndArrow }
                : { root: classes.outlinedRoot },
            }}
            helperText={helperText}
            multiline={multiline}
            minRows={rows}
            maxRows={rowsMax}
            data-test={`textDetail${dataTest}`}
            onWheel={handleWheelBlur}
            placeholder={placeholder}
          />
        </div>
      );
    }

    return (
      <TextField
        label={title}
        id={name}
        type={type}
        name={name}
        margin="dense"
        variant="filled"
        value={currentValue}
        onChange={handleFieldChange}
        onBlur={handleBlur}
        error={error}
        style={{ maxWidth: noMaxWidth ? 2500 : 280, width: '100%', top: 0, ...style }}
        inputProps={{
          min,
          max,
          step,
          ...disablePasswordManagerInputProps,
        }}
        InputProps={{
          ...endAdornmentProps,
          ...inputProps,
          classes: typeNumberNoEndArrow
            ? { underline: classes.underline, input: classes.noEndArrow }
            : { underline: classes.underline },
        }}
        InputLabelProps={{ className: classes.label }}
        helperText={helperText}
        multiline={multiline}
        minRows={rows}
        maxRows={rowsMax}
        hiddenLabel={isEmpty(title)}
        data-test={`textDetail${dataTest}`}
        onWheel={handleWheelBlur}
        placeholder={placeholder}
      />
    );
  }
  if (editMode && selectable && isNil(forcedValue)) {
    if (modern) {
      return (
        <div style={{ width: '100%' }}>
          {!isEmpty(title) && (
            <Typography className={classes.cardFieldLabel} style={titleStyle}>
              {title}
            </Typography>
          )}
          <TextField
            id={name}
            name={name}
            select
            margin="dense"
            variant="outlined"
            fullWidth
            value={getSafeValueInObject(values, name)}
            onChange={handleChange}
            onBlur={handleBlur}
            error={error}
            style={{ maxWidth: 280, top: 0, ...style, width: '100%' }}
            inputProps={{ min, max, step, ...disablePasswordManagerInputProps }}
            InputProps={{ classes: { root: classes.outlinedRoot } }}
            SelectProps={{ classes: { select: classes.select } }}
            helperText={helperText}
            data-test={`textDetail${dataTest}`}
            placeholder={placeholder}
          >
            {selectableValues.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </div>
      );
    }

    return (
      <TextField
        label={title}
        id={name}
        name={name}
        select
        margin="dense"
        variant="filled"
        value={getSafeValueInObject(values, name)}
        onChange={handleChange}
        onBlur={handleBlur}
        error={error}
        style={{ maxWidth: 280, top: 0, ...style, width: '100%' }}
        inputProps={{
          min,
          max,
          step,
          ...disablePasswordManagerInputProps,
        }}
        InputProps={{ classes: { underline: classes.underline } }}
        InputLabelProps={{ className: classes.label }}
        SelectProps={{ classes: { select: classes.select } }}
        helperText={helperText}
        multiline={multiline}
        minRows={rows}
        maxRows={rowsMax}
        data-test={`textDetail${dataTest}`}
        onWheel={(e) => {
          if (isNumberField && e.target instanceof HTMLElement) {
            e.target.blur();
          }
        }}
        placeholder={placeholder}
      >
        {selectableValues.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    );
  }
  if (editMode && selectable && !isNil(forcedValue)) {
    if (modern) {
      return (
        <div style={{ width: '100%' }}>
          {!isEmpty(title) && (
            <Typography className={classes.cardFieldLabel} style={titleStyle}>
              {title}
            </Typography>
          )}
          <TextField
            id={name}
            type={type}
            name={name}
            margin="dense"
            variant="outlined"
            fullWidth
            value={forcedValue}
            onChange={onChangeObserver}
            onBlur={handleBlur}
            error={error}
            style={{ maxWidth: noMaxWidth ? 2500 : 280, width: '100%', top: 0, ...style }}
            inputProps={{ min, max, step, ...disablePasswordManagerInputProps }}
            InputProps={{ ...endAdornmentProps, ...inputProps, classes: { root: classes.outlinedRoot } }}
            helperText={helperText}
            multiline={multiline}
            minRows={rows}
            maxRows={rowsMax}
            data-test={`textDetail${dataTest}`}
            onWheel={(e) => {
              if (isNumberField && e.target instanceof HTMLElement) {
                e.target.blur();
              }
            }}
            placeholder={placeholder}
          />
        </div>
      );
    }

    return (
      <TextField
        label={title}
        id={name}
        type={type}
        name={name}
        margin="dense"
        variant="filled"
        value={forcedValue}
        onChange={onChangeObserver}
        onBlur={handleBlur}
        error={error}
        style={{ maxWidth: noMaxWidth ? 2500 : 280, width: '100%', top: 0, ...style }}
        inputProps={{
          min,
          max,
          step,
          ...disablePasswordManagerInputProps,
        }}
        InputProps={{ ...endAdornmentProps, ...inputProps, classes: { underline: classes.underline } }}
        InputLabelProps={{ className: classes.label }}
        helperText={helperText}
        multiline={multiline}
        minRows={rows}
        maxRows={rowsMax}
        data-test={`textDetail${dataTest}`}
        onWheel={(e) => {
          if (isNumberField && e.target instanceof HTMLElement) {
            e.target.blur();
          }
        }}
        placeholder={placeholder}
      />
    );
  }

  if (editMode && !selectable && !isNil(forcedValue)) {
    const handleForcedFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const targetValue = e.target ? e.target.value : e.currentTarget.value;
      const value = isNumberField && targetValue !== '' ? getClampedNumberValue(targetValue, min, max) : targetValue;
      if (!isNil(onChangeObserver)) {
        onChangeObserver(value);
      }
    };

    if (modern) {
      return (
        <div style={{ width: '100%' }}>
          {!isEmpty(title) && (
            <Typography className={classes.cardFieldLabel} style={titleStyle}>
              {title}
            </Typography>
          )}
          <TextField
            id={name}
            type={type}
            name={name}
            margin="dense"
            variant="outlined"
            fullWidth
            value={forcedValue}
            disabled={disabled}
            onChange={handleForcedFieldChange}
            onBlur={handleBlur}
            error={error}
            style={{ maxWidth: noMaxWidth ? 2500 : 280, width: '100%', top: 0, ...style }}
            inputProps={{ min, max, step, ...disablePasswordManagerInputProps }}
            InputProps={{
              ...endAdornmentProps,
              ...inputProps,
              classes: typeNumberNoEndArrow
                ? { root: classes.outlinedRoot, input: classes.noEndArrow }
                : { root: classes.outlinedRoot },
            }}
            helperText={helperText}
            multiline={multiline}
            minRows={rows}
            maxRows={rowsMax}
            data-test={`textDetail${dataTest}`}
            onWheel={(e) => {
              if (isNumberField && e.target instanceof HTMLElement) {
                e.target.blur();
              }
            }}
            placeholder={placeholder}
          />
        </div>
      );
    }

    return (
      <TextField
        label={title}
        id={name}
        type={type}
        name={name}
        margin="dense"
        variant="filled"
        value={forcedValue}
        disabled={disabled}
        onChange={handleForcedFieldChange}
        onBlur={handleBlur}
        error={error}
        style={{ maxWidth: noMaxWidth ? 2500 : 280, width: '100%', top: 0, ...style }}
        inputProps={{
          min,
          max,
          step,
          ...disablePasswordManagerInputProps,
        }}
        InputProps={{
          ...endAdornmentProps,
          ...inputProps,
          classes: typeNumberNoEndArrow
            ? { underline: classes.underline, input: classes.noEndArrow }
            : { underline: classes.underline },
        }}
        InputLabelProps={{ className: classes.label }}
        helperText={helperText}
        multiline={multiline}
        minRows={rows}
        maxRows={rowsMax}
        hiddenLabel={isEmpty(title)}
        data-test={`textDetail${dataTest}`}
        onWheel={(e) => {
          if (isNumberField && e.target instanceof HTMLElement) {
            e.target.blur();
          }
        }}
        placeholder={placeholder}
      />
    );
  }

  if (modern) {
    return (
      <>
        <Typography className={classes.cardFieldLabel} style={titleStyle}>
          {title}
        </Typography>
        {!children ? (
          <Typography
            style={{ ...style, color: Colors.CLASSICAL_BLACK, fontSize: 16 }}
            data-test={`textDetail${dataTest}`}
          >
            {text}
          </Typography>
        ) : (
          children
        )}
      </>
    );
  }

  return (
    <>
      <Typography style={{ color: Colors.LIGHT_BLUE_GREY, fontSize: 12, ...titleStyle }}>{title}</Typography>
      {!children ? (
        <Typography
          style={{ ...style, color: Colors.CLASSICAL_BLACK, fontSize: 16 }}
          data-test={`textDetail${dataTest}`}
        >
          {text}
        </Typography>
      ) : (
        children
      )}
    </>
  );
};
